<template>
  <global-modal name="event-modal-montage-passes" @before-open="beforeOpen">
    <Modal
      class="event-modal-montage-passes"
      @close="$modal.hide('event-modal-montage-passes')"
    >
      <EventModalPassesHeader
        :options="header"
        @print="printAllPasses"
        @download="downloadAllPasses"
        :hide-quota="
          Number(getLocaleValue(exhibition, 'create_install_pass_disabled'))
        "
      >
        <template slot="title">
          <span v-html="$t('event.passes.montage.title')"></span>
        </template>

        <template
          slot="deadlines-text"
          v-if="
            installationStartDate &&
              installationEndDate &&
              dismantingStartDate &&
              dismantingEndDate
          "
        >
          {{
            $t("event.passes.montage.installationPeriod", {
              from: installationStartDate.format("DD.MM.YYYY"),
              to: installationEndDate.format("DD.MM.YYYY")
            })
          }}

          <br />

          {{
            $t("event.passes.montage.dismantingPeriod", {
              from: dismantingStartDate.format("DD.MM.YYYY"),
              to: dismantingEndDate.format("DD.MM.YYYY")
            })
          }}
        </template>

        <template
          v-if="getLocaleValue(exhibition, 'install_pass_info_1').length"
          slot="pass-warning"
        >
          <span
            v-html="getLocaleValue(exhibition, 'install_pass_info_1')"
          ></span>
        </template>

        <template
          v-if="getLocaleValue(exhibition, 'install_pass_info_2').length"
          slot="pass-warning2"
        >
          <span
            v-html="getLocaleValue(exhibition, 'install_pass_info_2')"
          ></span>
        </template>
        <template slot="rules-content" v-if="!isEventMontage">
          <DeliveryRules />
        </template>

        <template slot="quota-text">
          {{ $t("event.passes.montage.quotaText") }}
        </template>

        <template
          v-if="secondsFromDeadlineInstallationPrint >= 0 && header.quota.count"
          slot="warning-message"
        >
          <icon name="warning--no-color" />
          {{
            $t("event.passes.montage.warningMessage", {
              date: passDeadline.startInstallationPrint.format("DD.MM.YYYY")
            })
          }}
        </template>
        <template slot="pass_terms">
          <div v-html="getLocaleValue(exhibition, 'install_pass_info_3')"></div>
          <br />
          <LinkIcon
            v-if="getLocaleValue(exhibition, 'install_pass_terms').length"
            icon="doc"
            bold
            grey
            :href="
              exhibition.website +
                getLocaleValue(exhibition, 'install_pass_terms')
            "
            target="_blank"
          >
            {{ $t("event.passes.car.appForms") }}
          </LinkIcon>
        </template>
      </EventModalPassesHeader>
      <div class="loader-wrap" v-if="isPassLoading">
        <Loader is-absolute white opacity />
      </div>

      <template
        v-else-if="
          !Number(getLocaleValue(exhibition, 'create_install_pass_disabled'))
        "
      >
        <EventModalPassesAddForm
          class="event-modal-montage-passes-add"
          v-show="
            hasAccessToWrite &&
              (isAddFormShown || !montagePasses.length) &&
              diffInSeconds(passDeadline.installation) >= 0 &&
              !isQuotaFull
          "
          package-upload
          package-upload-montage
          :package-upload-template-src="
            $t('event.passes.montage.downloadBlank')
          "
          :package-upload-template-size="
            $t('event.passes.montage.packageUploadSize')
          "
          :show-hide="!!montagePasses.length"
          :is-submitting="isAddFormSubmitting"
          :has-errors="errors.any()"
          @package-upload-handler="uploadPackage"
          @submit="addPass"
          @hide="isAddFormShown = false"
        >
          <template slot="form-title">
            {{ $t("event.passes.montage.addTitle") }}
          </template>

          <h2 class="event-modal-passes-add-form__title">
            {{ $t("event.passes.montage.applicationTitle") }}
          </h2>

          <div class="event-modal-passes-add-form__row flex">
            <div class="event-modal-passes-add-form__row-col _380">
              <InputLabel>
                <template #label>
                  {{ $t("event.passes.participant.companyName") }}
                </template>

                <input
                  v-model="companyName"
                  type="text"
                  name="companyName"
                  v-validate="'required'"
                />

                <template v-if="errors.has('companyName')" slot="error">
                  {{ errors.first("companyName") }}
                </template>
              </InputLabel>
            </div>
            <div class="event-modal-passes-add-form__row-col _380">
              <InputLabel>
                <template #label>
                  {{ $t("common.lastName") }}
                </template>

                <input
                  type="text"
                  v-model="lastName"
                  name="lastName"
                  v-validate="'required|custom_alpha|min:1|max:50'"
                />

                <template slot="error" v-if="errors.has('lastName')">
                  {{ errors.first("lastName") }}
                </template>
              </InputLabel>
            </div>
          </div>

          <div class="event-modal-passes-add-form__row flex">
            <div class="event-modal-passes-add-form__row-col _380">
              <InputLabel>
                <template #label>
                  {{ $t("common.firstName") }}
                </template>

                <input
                  type="text"
                  v-model="firstName"
                  name="firstName"
                  v-validate="'required|custom_alpha|min:1|max:50'"
                />

                <template slot="error" v-if="errors.has('firstName')">
                  {{ errors.first("firstName") }}
                </template>
              </InputLabel>
            </div>
            <div class="event-modal-passes-add-form__row-col _380">
              <InputLabel>
                <template slot="label">
                  {{ $t("common.middleName") }}
                </template>
                <input
                  type="text"
                  name="middleName"
                  v-model="middleName"
                  v-validate="'custom_alpha|min:1|max:50'"
                />

                <template slot="error" v-if="errors.has('middleName')">{{
                  errors.first("middleName")
                }}</template>
              </InputLabel>
            </div>
          </div>
        </EventModalPassesAddForm>

        <EventModalPassesUsers
          class="event-modal-montage-passes-content"
          type="installation"
          :users="montagePasses"
          :deadline="passDeadline.installation"
          :deadlineStartPrint="passDeadline.startInstallationPrint"
          :deadlineEndPrint="passDeadline.endInstallationPrint"
          :allow-adding-pass="!isQuotaFull"
          :is-loading="isLoadingWithOpacity"
          :sorting="montagePassSorting"
          @add="isAddFormShown = true"
          @delete="deletePass"
          @delete-all="deleteAllPasses"
          @print="printPass"
          @download="downloadPass"
          @upload="sortingPasses"
          @sortPasses="sortingPasses"
        />
        <div class="event-modal-montage-passes-content">
          <pagination
            class="event-modal-montage-passes__pagination"
            v-if="montagePassesPaginationLength > 1"
            :page="currentPage"
            :page-count="montagePassesPaginationLength"
            :total-visible="paginationPageAmount"
            @change="paginate"
          />
        </div>
      </template>
    </Modal>
  </global-modal>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters } from "vuex";
import printJS from "print-js";
import apiPersonalPass from "@/api/personalPass";
import getError from "@/helpers/error";
import diffInSeconds from "@/helpers/diffInSeconds";
import { getLocaleValue } from "@/helpers/common";
//TODO: Найти другую валидацию полей. Из за vee-validate приходится использовать v-show вместе v-if родительского компонента.
import Modal from "@/components/Modal/Modal.vue";
import DeliveryRules from "@/components/Custom/DeliveryRules.vue";
import {
  Loader,
  Pagination,
  InputLabel,
  LinkIcon
} from "@v2/shared/ui/components";
import { i18n } from "@v2/shared/config";
import EventModalPassesHeader from "./EventModalPassesHeader.vue";
import EventModalPassesAddForm from "./EventModalPassesAddForm.vue";
import EventModalPassesUsers from "./EventModalPassesUsers.vue";

export default {
  name: "EventModalMontagePasses",

  components: {
    LinkIcon,
    Pagination,
    Loader,
    DeliveryRules,
    Modal,
    InputLabel,

    EventModalPassesHeader,
    EventModalPassesAddForm,
    EventModalPassesUsers
  },

  data: () => ({
    isAddFormShown: false,
    isAddFormSubmitting: false,

    lastName: "",
    firstName: "",
    middleName: "",
    companyName: "",
    isPassLoading: false,
    locale: i18n.locale,
    isLoadingWithOpacity: false,
    currentPage: 1
  }),

  computed: {
    ...mapState("event", ["event"]),
    ...mapState("passes", [
      "montagePasses",
      "montagePassesPaginationLength",
      "montagePassSorting",
      "montagePassesAmount"
    ]),
    ...mapGetters("passes", ["montageSelectedSorting"]),
    ...mapGetters("event", [
      "exhibition",
      "hasAccessToWrite",
      "eventId",
      "passDeadline",
      "eventPavilion",
      "isEventMontage"
    ]),
    minDate() {
      return moment("1900-01-01 00:00:00");
    },
    paginationPageAmount() {
      return window.innerWidth > 570 ? 5 : 2;
    },
    installationStartDate() {
      let date = this.event?.exhibition?.installation_started_at;
      return date ? moment(date) : null;
    },

    installationEndDate() {
      let date = this.event?.exhibition?.installation_ended_at;
      return date ? moment(date) : null;
    },

    dismantingStartDate() {
      let date = this.event?.exhibition?.dismantling_started_at;
      return date ? moment(date) : null;
    },

    dismantingEndDate() {
      let date = this.event?.exhibition?.dismantling_ended_at;
      return date ? moment(date) : null;
    },

    header() {
      return {
        quota: {
          count: this.montagePassesAmount,
          total: this.availableMontagePassesAmount
        },
        deadline: this.passDeadline.installation,
        deadlineStartPrint: this.passDeadline.startInstallationPrint,
        deadlineEndPrint: this.passDeadline.endInstallationPrint,
        passes: this.montagePasses
      };
    },

    availableMontagePassesAmount() {
      if (!this.event || !this.event.quota) return 0;

      return this.event.quota.installation;
    },

    isQuotaFull() {
      return this.header.quota.count === this.header.quota.total;
    },

    secondsFromDeadlineInstallationPrint() {
      if (!this.passDeadline && !this.passDeadline.startInstallationPrint) {
        return Infinity;
      }

      return diffInSeconds(this.passDeadline.startInstallationPrint);
    }
  },

  methods: {
    diffInSeconds,
    getLocaleValue,
    autoFillCompanyName() {
      const company = this.$store.getters["event/company"];
      if (company)
        this.companyName =
          i18n.locale === "en" ? company.name_eng : company.name_rus;
    },
    async updateMontagePass() {
      this.isPassLoading = true;
      await this.$store.dispatch("passes/fetchParticipantPasses", {
        eventId: this.eventId,
        page: this.currentPage,
        type: "installation",
        lastname: this.montageSelectedSorting.sorting.sort_by,
        order_by: this.montageSelectedSorting.order_by
      });
      this.isPassLoading = false;
    },
    beforeOpen() {
      this.autoFillCompanyName();
      this.updateMontagePass();
    },
    async paginate(page) {
      this.isLoadingWithOpacity = true;
      this.currentPage = page;
      await this.$store.dispatch("passes/fetchParticipantPasses", {
        eventId: this.eventId,
        type: "installation",
        page: this.currentPage,
        lastname: this.montageSelectedSorting.sorting.sort_by,
        order_by: this.montageSelectedSorting.order_by
      });
      this.isLoadingWithOpacity = false;
    },
    async sortingPasses() {
      this.isLoadingWithOpacity = true;
      await this.$store.dispatch("passes/fetchParticipantPasses", {
        eventId: this.eventId,
        type: "installation",
        page: this.currentPage,
        lastname: this.montageSelectedSorting.sorting.sort_by,
        order_by: this.montageSelectedSorting.sorting.order_by
      });
      this.isLoadingWithOpacity = false;
    },
    addPass() {
      this.$validator.validateAll().then(result => {
        if (!result) {
          return;
        }

        this.isAddFormSubmitting = true;

        this.$store
          .dispatch("passes/addMontagePass", {
            eventId: this.event.id,
            pass: {
              company_name: this.companyName,
              lastname: this.lastName,
              name: this.firstName,
              middle_name: this.middleName
            }
          })
          .then(() => {
            this.lastName = "";
            this.firstName = "";
            this.middleName = "";
            this.$validator.reset();

            this.isAddFormShown = false;
          })
          .catch(error => {
            this.$store.dispatch("common/setErrorMessage", getError(error));
          })
          .finally(() => {
            this.isAddFormSubmitting = false;
          });
      });
    },

    async deletePass(pass) {
      this.isLoadingWithOpacity = true;
      await this.$store.dispatch("passes/removeInstallationPass", {
        eventId: this.eventId,
        passId: pass.id
      });
      this.isLoadingWithOpacity = false;
    },

    async deleteAllPasses() {
      this.isLoading = true;
      await this.$store.dispatch("passes/removeAllInstallationPasses", {
        eventId: this.eventId
      });
      this.isLoading = false;
    },

    async uploadPackage(file) {
      try {
        await this.$store.dispatch("passes/uploadInstallationPasses", {
          eventId: this.event.id,
          file
        });
      } catch (error) {
        await this.$store.dispatch("common/setErrorMessage", getError(error));
      } finally {
        this.isAddFormShown = false;
      }
    },

    async downloadPass(pass, print = false) {
      try {
        this.isLoadingWithOpacity = true;
        const response = await apiPersonalPass.downloadPersonalPass(
          this.eventId,
          pass.id
        );
        let blob = new Blob([response], {
          type: "application/pdf"
        });
        const url = window.URL.createObjectURL(blob);
        if (print) {
          printJS(url);
        } else {
          let link = document.createElement("a");
          link.href = url;
          link.download = `Монтажный пропуск застройщика ${pass.name} ${pass.lastname} (${pass.id}).pdf`;
          link.click();
        }
      } catch (e) {
        await this.$store.dispatch("common/setErrorMessage", getError(e));
      } finally {
        this.isLoadingWithOpacity = false;
      }
    },

    printPass(pass) {
      this.downloadPass(pass, true);
    },

    async downloadAllPasses(print = false) {
      try {
        this.isLoadingWithOpacity = true;
        const response = await apiPersonalPass.downloadAllPersonalPass(
          this.eventId,
          "installation"
        );
        let blob = new Blob([response], {
          type: "application/pdf"
        });
        const url = window.URL.createObjectURL(blob);
        if (print) {
          printJS(url);
        } else {
          let link = document.createElement("a");
          link.href = url;
          link.download = `Пакет монтажных пропусков застройщиков.pdf`;
          link.click();
        }
      } catch (e) {
        await this.$store.dispatch("common/setErrorMessage", getError(e));
      } finally {
        this.isLoadingWithOpacity = false;
      }
    },

    printAllPasses() {
      this.downloadAllPasses(true);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-modal-montage-passes {
  @mixin modal-container() {
    width: 780px;
    padding-left: 80px;

    @include respond-down(md) {
      width: 100%;
      padding: 0 0 31px 0;
      box-sizing: border-box;
    }
  }

  &-add {
    background: rgba(#f9f9f9, 0.5);
  }
  &__pagination {
    margin: 30px auto 30px;
    @include respond-down(lg) {
      margin: 20px auto 20px;
    }
  }
  &-content {
    @include modal-container();

    @include respond-down(lg) {
      padding: 0 24px 40px 24px;

      width: 100%;

      box-sizing: border-box;
    }

    @include respond-down(md) {
      padding: 0 0 31px 0;

      width: 100%;

      box-sizing: border-box;
    }
  }

  &-add + &-content:not(:empty) {
    margin-top: 40px;
  }
}
.loader-wrap {
  position: relative;
  min-height: 200px;
  max-width: 870px;
}
</style>
